/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useEffect } from 'react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import { referralCodeAtom } from '@root/entities/referral'
import { reatomChangeReferralCodeForm } from '@root/features/referral/atoms/reatom-change-referral-code'
import { changeReferralCodeDialogOpenAtom } from '@root/features/referral/atoms/reatom-referral-dialogs'
import { useTranslation } from '@root/shared/localization'
import { Drawer, Separator } from '@root/shared/ui-kit'

import { ReferralCodeForm } from '../../desktop/common/referral-code-form'
import { ReferralCodeInfo } from '../../desktop/common/referral-code-info'

import type { ReferralCodeSubmitValues } from '../../desktop/common/referral-code-form'

export const ChangeReferralCodeDrawer = () => {
  const [open, setOpen] = useAtom(changeReferralCodeDialogOpenAtom)

  return (
    <AnimatePresence>
      {open ? <Inner open={open} onOpenChange={setOpen} /> : undefined}
    </AnimatePresence>
  )
}

type InnerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const Inner = ({ open, onOpenChange }: InnerProps) => {
  const { t } = useTranslation()
  const model = useMemo(() => reatomChangeReferralCodeForm(), [])
  const [requestState] = useAtom(model.requestState)
  const execute = useAction(model.change)
  const [currentReferralCode] = useAtom(referralCodeAtom)

  const handleSubmit = (values: ReferralCodeSubmitValues) => {
    execute({
      referralCode: values.referralCode,
      walletId: values.wallet.id,
    })
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChange(false)
    }
  }, [requestState, onOpenChange])

  return (
    <Root backgroundGradient="primary" open={open} onOpenChange={onOpenChange}>
      <Content>
        <Header>
          <Title>{t('referral.change-referral-code')}</Title>
          {currentReferralCode ? (
            <ReferralCodeInfo referralCode={currentReferralCode} withLabel />
          ) : undefined}
          <Separator />
          <Note>{t('referral.change-referral-note')}</Note>
        </Header>
        <Body>
          <Separator />
          <ReferralCodeForm
            device="mobile"
            onCancel={() => onOpenChange(false)}
            existingReferralCode={currentReferralCode}
            state={requestState}
            onSubmit={handleSubmit}
          />
        </Body>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  padding-bottom: var(--safe-bottom);
`
const Title = styled(Typography).attrs({
  variant: 'h4',
  component: 'h2',
})`
  font-size: 18px;
  line-height: 140%;
  color: var(--text-12);
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px 16px;
  text-align: center;
  gap: 16px;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  gap: 16px;
  background: radial-gradient(
    106.14% 158.88% at 50% -46.89%,
    var(--backgroundA-4) 0%,
    var(--backgroundA-1) 100%
  );
  backdrop-filter: blur(16px);
`

const Note = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  color: var(--textA-9);
  text-align: center;
`
